import { apiFetch } from './http';

// ═══════════════════════════════════════════════════════════════════
// ASKING THE SERVER TO OPEN A PAYMENT (#284).
//
// `POST /member/bookings/:id/payment`. The request carries nothing but the
// booking id — not the amount, not the merchant, not the payer. All three are
// authority the server derives, and a browser that could assert any of them
// could pay a krone for a court, pay into somebody else's bank account, or pay
// for a booking that is not its own.
//
// WHAT COMES BACK IS NOT A RECEIPT. It is permission to present a card: the
// provider's client secret, and the connected account the intent lives on. The
// paid state arrives later, through the webhook — `clubSession`-style optimism
// has no place here, because the one thing the browser must not decide is
// whether money moved.
// ═══════════════════════════════════════════════════════════════════

export interface OpenedBookingPayment {
  readonly paymentId: string;
  /**
   * What Stripe.js needs to present the Payment Element.
   *
   * `null` when the deployment has no provider configured for the browser half —
   * the payment was still opened server-side, and the caller must say «cannot
   * take card payment here» rather than mount an Element with nothing in it.
   */
  readonly clientSecret: string | null;
  /**
   * THE CONNECTED ACCOUNT. Required, not informational: under direct charges the
   * intent lives ON this account, so Stripe.js must be initialised with
   * `{ stripeAccount }` or the client secret does not resolve.
   */
  readonly stripeAccount: string;
  /**
   * WHICH METHODS THE INTENT NAMES (#508): `with_redirect` when the server
   * opened it with Vipps beside card, `in_page` otherwise. The sheet loads
   * Stripe.js with the Vipps beta only for the first — so a club without Vipps,
   * and the native app, run exactly the card path they always ran. Anything
   * the server did not say is `in_page`.
   */
  readonly methodScope: 'in_page' | 'with_redirect';
  /** Minor units — øre. The server's figure, never the screen's. */
  readonly amountMinor: number;
  readonly currency: string;
}

export type OpenBookingPaymentResult =
  | { readonly state: 'ok'; readonly payment: OpenedBookingPayment }
  /**
   * The server said no, with a code. Codes are never translated on the wire —
   * the browser decides the sentence (#519).
   */
  | {
      readonly state: 'refused';
      readonly reason: string;
      /** For `attempt_terms_changed`: the attempt that has to be resolved first. */
      readonly outstandingPaymentId?: string;
    }
  | { readonly state: 'error'; readonly status: number };

/**
 * Open — or re-open — the payment for one booking.
 *
 * SAFE TO CALL AGAIN. The server reuses an open attempt and asks the provider
 * with the same derived idempotency key, so a member who reloads or double-taps
 * gets the same PaymentIntent rather than a second one. That is why this needs no
 * client-side guard against a second call: the guarantee is the server's.
 *
 * Never throws for a refusal — «this booking is already paid» is an ordinary
 * answer the screen has to show. Transport failures do throw, because «the
 * request never happened» is a different thing from «the club said no».
 */
/**
 * WHAT IS BEING PAID FOR (docs/domains/ASSET_BOOKING.md, slice 2).
 *
 * A court booking — with whatever equipment rides with it, as ONE amount — is
 * addressed by its `book_…` id under `/member/bookings`; a standalone equipment
 * booking by its own id under `/member/asset-bookings`. Same three calls, same
 * answers, same one merchant: the centre.
 */
export type PaymentSubject = 'booking' | 'equipment';

function paymentPath(subject: PaymentSubject, id: string): string {
  const collection = subject === 'equipment' ? 'asset-bookings' : 'bookings';
  return `/member/${collection}/${encodeURIComponent(id)}/payment`;
}

export async function openBookingPayment(
  bookingId: string,
  subject: PaymentSubject = 'booking',
  /**
   * CAN THIS PAYER LEAVE THE PAGE AND COME BACK? (#508)
   *
   * True in a browser, false in the native shell — which cannot yet bring the
   * member back from the Vipps app (#1114). The ONE thing the request says: a
   * statement about the client, which the server combines with what it reads
   * about the merchant itself. It can only narrow what is offered.
   */
  options: { redirect?: boolean } = {},
): Promise<OpenBookingPaymentResult> {
  const res = await apiFetch(paymentPath(subject, bookingId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ redirect: options.redirect === true }),
  });
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;

  if (res.ok) {
    const stripeAccount = typeof body?.stripeAccount === 'string' ? body.stripeAccount : '';
    const amountMinor = typeof body?.amountMinor === 'number' ? body.amountMinor : 0;
    // A 200 that cannot actually be paid is worse than a refusal, because the
    // screen would open a sheet and blame Stripe. Checked here, once.
    if (stripeAccount === '' || amountMinor <= 0) {
      return { state: 'error', status: res.status };
    }
    return {
      state: 'ok',
      payment: {
        paymentId: String(body?.paymentId ?? ''),
        clientSecret: typeof body?.clientSecret === 'string' ? body.clientSecret : null,
        stripeAccount,
        methodScope: body?.methodScope === 'with_redirect' ? 'with_redirect' : 'in_page',
        amountMinor,
        currency: typeof body?.currency === 'string' ? body.currency : 'NOK',
      },
    };
  }

  // 409 and 404 both carry a code. 403 does too, but «not your booking» is not
  // something a member can act on, so it travels as the code it is.
  const reason = typeof body?.error === 'string' ? body.error : null;
  if (reason !== null) {
    return {
      state: 'refused',
      reason,
      ...(typeof body?.outstandingPaymentId === 'string'
        ? { outstandingPaymentId: body.outstandingPaymentId }
        : {}),
    };
  }
  return { state: 'error', status: res.status };
}

// ─────────────────────────────────────────────────────────────────────
// GIVING UP ON AN OUTSTANDING PAYMENT (#607)
//
// `DELETE /member/bookings/:id/payment`. It abandons the ATTEMPT, not the
// booking: the court stays the member's and the hold goes back to «waiting for
// payment», so the next attempt is opened on whatever the terms are now.
//
// WHY A SCREEN NEEDS IT. A live provider intent cannot be re-priced, so when a
// club changes its prices while somebody is paying, the server refuses the next
// attempt with `attempt_terms_changed` — correctly, and until this existed, for
// ever. The booking could be neither paid for nor cleared, which reads as a
// broken app rather than as a safety rail.
//
// IT IS NOT GUARANTEED TO SUCCEED, and the failure is the interesting one: if the
// card went through in the seconds before the member pressed cancel, the provider
// refuses the cancellation and the attempt goes to verification. `awaiting`
// carries that, because a screen must not say «cancelled» about a payment that
// may have taken money.
// ─────────────────────────────────────────────────────────────────────

export type AbandonBookingPaymentResult =
  /** The attempt is dead. The court is still theirs, and payable again. */
  | { readonly state: 'ok' }
  /**
   * The provider would not confirm the cancellation — most likely because it
   * succeeded. Reconciliation settles it; nothing here should.
   */
  | { readonly state: 'awaiting'; readonly reason: string }
  /** There was nothing outstanding to abandon. Not an error for a member. */
  | { readonly state: 'nothing_to_do' }
  /**
   * THE CLOCK GOT THERE FIRST (#607). The hold's fifteen minutes were already up,
   * so what happened was an expiry: the attempt is dead AND the court is gone.
   * Distinct from `ok` because the screen must not offer to pay for it again.
   */
  | { readonly state: 'expired'; readonly released: boolean }
  | { readonly state: 'error'; readonly status: number };

export async function abandonBookingPayment(
  bookingId: string,
  subject: PaymentSubject = 'booking',
): Promise<AbandonBookingPaymentResult> {
  let res: Response;
  try {
    res = await apiFetch(paymentPath(subject, bookingId), {
      method: 'DELETE',
    });
  } catch {
    // The request never happened, so nothing is known about the attempt. NOT
    // «cancelled» — the screen must keep offering the same way out.
    return { state: 'error', status: 0 };
  }
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (res.ok) {
    // The route answers 200 both for «cancelled» and for «there was nothing
    // open», and they are different things to a screen: one has just freed the
    // member to pay again, the other means the retry will fail the same way.
    return body?.cancelled === true ? { state: 'ok' } : { state: 'nothing_to_do' };
  }
  const reason = typeof body?.error === 'string' ? body.error : null;
  if (reason === 'awaiting_verification') return { state: 'awaiting', reason };
  if (reason === 'reservation_expired') {
    return { state: 'expired', released: body?.released === true };
  }
  return { state: 'error', status: res.status };
}

// ─────────────────────────────────────────────────────────────────────
// AND AFTERWARDS: «DID IT LAND?» (#284)
//
// Confirming the card in the page is not the end of the story — the booking
// becomes `paid` when the signed webhook arrives, which happens on another
// connection seconds later and tells the browser nothing. Without this the member
// app sat on «betaling bekreftes …» for ever, money taken, booking `pending`.
//
// A READ, and deliberately a different call from `openBookingPayment`: polling
// something that OPENS a payment would be asking the server to act in order to
// find out whether it already had.
// ─────────────────────────────────────────────────────────────────────

export type BookingPaymentStatus =
  /**
   * The server's own word. `paid` is the only one that means money moved.
   *
   * `expiresInMs` is how long the court is still held — **measured by the server**
   * (#607). A countdown must not derive it from `reservationExpiresAt` and the
   * device's own clock: a phone three minutes fast would show a member three
   * minutes they do not have. Absent when nothing is owed.
   */
  | {
      readonly state: 'ok';
      readonly paymentStatus: string;
      readonly expiresInMs?: number;
    }
  /**
   * THE BOOKING IS GONE. After #607 this is an ordinary outcome, not an error:
   * the hold lapsed and the sweep released the court. A screen must say so rather
   * than keep counting down to a reservation that no longer exists.
   */
  | { readonly state: 'gone' }
  /**
   * THE ROUTE IS NOT THERE (#1126, AUD-003). The API mounts `/member/…/payment`
   * only when a payment provider is configured, so a deployment without one — a
   * provider outage, a misconfigured environment, a client one release ahead of
   * its API — answers a BARE 404 with no `booking_not_found` body. That is not
   * «the hold lapsed»: the booking is still held and unpaid, and telling the
   * member their court was released would be false. Distinct from `unknown`
   * because polling will not change it.
   */
  | { readonly state: 'not_configured' }
  /** Not answered — a transport failure or a refusal. The caller keeps waiting. */
  | { readonly state: 'unknown' };

export async function readBookingPaymentStatus(
  bookingId: string,
  subject: PaymentSubject = 'booking',
): Promise<BookingPaymentStatus> {
  let res: Response;
  try {
    res = await apiFetch(paymentPath(subject, bookingId));
  } catch {
    // A poll that cannot reach the server has learned nothing, which is NOT the
    // same as «not paid» — the caller must keep waiting rather than conclude.
    return { state: 'unknown' };
  }
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  // 404 WITH THE SERVER'S WORD IS «IT EXPIRED», and it is told apart from every
  // other refusal. The row the poll is about has been deleted, which is exactly
  // what losing a hold looks like from here — reading it as `unknown` would leave
  // the screen waiting for a booking that is never coming back.
  //
  // THE WORD IS REQUIRED (#1126). The route answers `{ error: 'booking_not_found' }`
  // for a missing row; a 404 WITHOUT it is the router saying the path does not
  // exist — no provider configured — and the booking is still there. Two facts
  // shared one status code, and the screen said «frigitt» about a held court.
  if (res.status === 404) {
    return body?.error === 'booking_not_found' ? { state: 'gone' } : { state: 'not_configured' };
  }
  if (!res.ok) return { state: 'unknown' };
  const paymentStatus = typeof body?.paymentStatus === 'string' ? body.paymentStatus : null;
  if (paymentStatus === null) return { state: 'unknown' };
  const expiresInMs = typeof body?.expiresInMs === 'number' ? body.expiresInMs : undefined;
  return { state: 'ok', paymentStatus, ...(expiresInMs === undefined ? {} : { expiresInMs }) };
}
